import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '@fetmode/api-client';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';

import { useCoinShop } from '../wallet/index.js';
import { StepFormChrome } from './StepFormChrome.js';
import './PrivateShowRfpModal.css';

export interface CustomVideoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelUsername: string;
}

const STEP_BRIEF = 0;
const STEP_OFFER = 1;

export function CustomVideoRequestModal({
  isOpen,
  onClose,
  modelUsername,
}: CustomVideoRequestModalProps): ReactElement | null {
  const { api, user } = useAuth();
  const { openAuthModal, requireAuth } = useAuthModal();
  const { requireSpendCoins } = useCoinShop();
  const [step, setStep] = useState(STEP_BRIEF);
  const [brief, setBrief] = useState('');
  const [offer, setOffer] = useState('200');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStep(STEP_BRIEF);
    setError(null);
    setSuccess(false);
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  function submit(): void {
    requireAuth(() => {
      const offeredCoins = Number.parseInt(offer, 10);
      if (brief.trim().length < 10 || !Number.isFinite(offeredCoins) || offeredCoins < 10) {
        setError(t('common.storefront.customVideoInvalid'));
        if (brief.trim().length < 10) {
          setStep(STEP_BRIEF);
        }
        return;
      }
      const runSubmit = (): void => {
        setIsSaving(true);
        setError(null);
        void api
          .createCustomVideoOrder(modelUsername, {
            brief: brief.trim(),
            offeredCoins,
          })
          .then(() => {
            setSuccess(true);
            setBrief('');
          })
          .catch((err: unknown) => {
            setError(
              err instanceof ApiError ? err.message : t('common.storefront.customVideoError'),
            );
          })
          .finally(() => {
            setIsSaving(false);
          });
      };
      void requireSpendCoins(offeredCoins, runSubmit).then((ok) => {
        if (ok) {
          runSubmit();
        }
      });
    }, 'purchase');
  }

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="show-room-modal private-rfp-modal" role="dialog" aria-modal="true">
      <button
        type="button"
        className="show-room-modal__backdrop"
        aria-label={t('common.show.privateShowModalClose')}
        onClick={onClose}
      />
      <div className="show-room-modal__panel">
        <header className="show-room-modal__header">
          <h2 className="show-room-modal__title">{t('common.storefront.customVideoTitle')}</h2>
          <button
            type="button"
            className="show-room-modal__close"
            onClick={onClose}
            aria-label={t('common.show.privateShowModalClose')}
          >
            ×
          </button>
        </header>

        <div className="show-room-modal__body">
          {!user ? (
            <p className="show-room-modal__muted">
              <button
                type="button"
                className="show-room-modal__text-action"
                onClick={() => {
                  openAuthModal({ mode: 'login', intent: 'purchase' });
                }}
              >
                {t('common.storefront.customVideoSignIn')}
              </button>
            </p>
          ) : success ? (
            <p className="private-rfp-modal__success">{t('common.storefront.customVideoSuccess')}</p>
          ) : (
            <>
              <p className="show-room-modal__muted">{t('common.storefront.customVideoHint')}</p>
              <StepFormChrome
                step={step}
                totalSteps={2}
                stepLabel={
                  step === STEP_BRIEF
                    ? t('common.storefront.customVideoStepBrief')
                    : t('common.storefront.customVideoStepOffer')
                }
                error={error}
                isSubmitting={isSaving}
                isLastStep={step === STEP_OFFER}
                canGoNext={brief.trim().length >= 10}
                submitLabel={
                  isSaving
                    ? t('common.storefront.customVideoSaving')
                    : t('common.storefront.customVideoSubmit')
                }
                onBack={() => {
                  setError(null);
                  setStep(STEP_BRIEF);
                }}
                onNext={() => {
                  if (brief.trim().length < 10) {
                    setError(t('common.storefront.customVideoInvalid'));
                    return;
                  }
                  setError(null);
                  setStep(STEP_OFFER);
                }}
                onSubmit={submit}
              >
                {step === STEP_BRIEF ? (
                  <label className="private-rfp-modal__field">
                    {t('common.storefront.customVideoBriefLabel')}
                    <textarea
                      className="fm-input"
                      rows={5}
                      maxLength={2000}
                      value={brief}
                      placeholder={t('common.storefront.customVideoBrief')}
                      onChange={(event) => {
                        setBrief(event.target.value);
                      }}
                    />
                  </label>
                ) : (
                  <>
                    <label className="private-rfp-modal__field">
                      {t('common.storefront.customVideoOffer')}
                      <input
                        className="fm-input"
                        type="number"
                        min={10}
                        value={offer}
                        onChange={(event) => {
                          setOffer(event.target.value);
                        }}
                      />
                    </label>
                    <ul className="step-form__review">
                      <li>
                        <span>{t('common.storefront.customVideoBriefLabel')}</span>
                        <strong>
                          {brief.trim().length > 48
                            ? `${brief.trim().slice(0, 48)}…`
                            : brief.trim()}
                        </strong>
                      </li>
                    </ul>
                  </>
                )}
              </StepFormChrome>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
